import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import commonActions from '../actions/common.actions'
import { parseErrorFromArrayBuffer } from '../components/common/errorParser'
import { PinVerificationChannel } from '../proto/generated/identity'
import { ForgetPassword, SendEmailOtp } from '../services/apis/auth'
import { setPin } from '../services/apis/pin'
import { sendPinVerificationOtp } from '../services/apis/user'

export const PIN_DIGITS = 4
export const MIN_PASSWORD_LENGTH = 8
export const EMAIL_OTP_LENGTH = 6
export const EMAIL_FORMAT = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

export function sanitizeDigitInput(value: string, maxLength: number): string {
	return value.replace(/\D/g, '').slice(0, maxLength)
}

export function getPinPairError(pin: string, confirmPin: string): string | null {
	if (!/^\d{4}$/.test(pin) || !/^\d{4}$/.test(confirmPin)) {
		return `PIN must be exactly ${PIN_DIGITS} digits`
	}
	if (pin !== confirmPin) {
		return 'PINs do not match'
	}
	return null
}

export function getPasswordPairError(password: string, confirmPassword: string): string | null {
	if (!password || !confirmPassword) {
		return 'Please fill all password fields'
	}
	if (password.length < MIN_PASSWORD_LENGTH) {
		return `Password length should be at least ${MIN_PASSWORD_LENGTH} characters`
	}
	if (password !== confirmPassword) {
		return 'Passwords do not match'
	}
	return null
}

export async function requestPinEmailOtp(): Promise<string> {
	const res = await sendPinVerificationOtp({
		body: { channel: PinVerificationChannel.PIN_VERIFICATION_CHANNEL_EMAIL },
	})
	if (!res.requestId) {
		throw new Error('Failed to start email verification')
	}
	return res.requestId
}

/** OTP is delivered to email and mobile (when available). */
export function getOtpDeliveryMessage(email?: string | null, mobile?: string | null, countryCode?: string | null): string {
	const destinations: string[] = []
	if (email) destinations.push(email)
	if (mobile) destinations.push(`+${countryCode || '91'}-${mobile}`)
	if (destinations.length === 0) {
		return 'OTP sent to your email and mobile (if available)'
	}
	if (destinations.length === 1) {
		return `OTP sent to ${destinations[0]}`
	}
	return `OTP sent to ${destinations[0]} and ${destinations[1]}`
}

/** Initial PIN setup using mobile-verify grant when available. */
export async function savePinInitial(pin: string, pinSetupToken?: string): Promise<string> {
	const op = await setPin({
		pin,
		pinSetupToken: pinSetupToken || '',
		verificationRequestId: '',
		verificationOtp: '',
	})
	if (!op.success) {
		throw new Error(op.message || 'Could not save PIN')
	}
	const { persistUnlockedAccessToken } = await import('../services/auth/tokenRefresh')
	persistUnlockedAccessToken(op.accessToken)
	return op.message || 'PIN saved successfully'
}

export async function savePinWithEmailOtp(args: {
	pin: string
	verificationRequestId: string
	verificationOtp: string
}): Promise<string> {
	const op = await setPin({
		pin: args.pin,
		verificationRequestId: args.verificationRequestId,
		verificationOtp: args.verificationOtp,
		pinSetupToken: '',
	})
	if (!op.success) {
		throw new Error(op.message || 'Could not save PIN')
	}
	const { persistUnlockedAccessToken } = await import('../services/auth/tokenRefresh')
	persistUnlockedAccessToken(op.accessToken)
	return op.message || 'PIN saved successfully'
}

export async function requestPasswordEmailOtp(email: string): Promise<string> {
	const response = await SendEmailOtp({ body: { email }, purpose: 'password-reset' })
	const requestId = response.data?.requestId || ''
	if (!requestId) {
		throw new Error('Failed to send OTP. retry later')
	}
	return requestId
}

export async function changePasswordWithEmailOtp(args: {
	email: string
	newPassword: string
	emailVerificationRequestId: string
	emailVerificationOtp: string
}): Promise<void> {
	await ForgetPassword({
		body: {
			email: args.email,
			newPassword: args.newPassword,
			emailVerificationRequestId: args.emailVerificationRequestId,
			emailVerificationOtp: args.emailVerificationOtp,
		},
	})
}

function isAxiosStatusFallbackMessage(message: string): boolean {
	return /^Request failed with status code \d+$/i.test(message.trim())
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
	if (error && typeof error === 'object') {
		const { message } = parseErrorFromArrayBuffer(error)
		if (message && !isAxiosStatusFallbackMessage(message)) {
			return cleanApiErrorMessage(message)
		}
		const axiosMessage = (error as { response?: { data?: { message?: string } } }).response?.data?.message
		if (typeof axiosMessage === 'string' && axiosMessage.trim()) {
			return cleanApiErrorMessage(axiosMessage)
		}
		const plainMessage = (error as { message?: unknown }).message
		if (
			typeof plainMessage === 'string' &&
			plainMessage.trim() &&
			plainMessage !== 'An error occurred' &&
			!isAxiosStatusFallbackMessage(plainMessage)
		) {
			return cleanApiErrorMessage(plainMessage)
		}
	}
	if (error instanceof Error && error.message && !isAxiosStatusFallbackMessage(error.message)) {
		return cleanApiErrorMessage(error.message)
	}
	return fallback
}

function cleanApiErrorMessage(message: string): string {
	const trimmed = message.replace(/^\u0010?/, '').trim()
	const withoutCode = trimmed.replace(
		/^(ERROR_CODES_UNKNOWN|INVALID_ARGUMENT|VALIDATION_ERROR|AUTHENTICATION_FAILED|ENTITY_NOT_FOUND|PERMISSION_DENIED)\s*/i,
		''
	)
	return withoutCode.trim() || trimmed
}

/** Shared snackbar helpers for Personal security dialogs. */
export function useSecurityNotify() {
	const dispatch = useDispatch()

	const showError = useCallback(
		(message: string) => {
			commonActions.setApiError(dispatch, { error: true, message, errorType: 'error' })
		},
		[dispatch]
	)

	const showSuccess = useCallback(
		(message: string) => {
			commonActions.setApiError(dispatch, { error: true, message, errorType: 'success' })
		},
		[dispatch]
	)

	return { showError, showSuccess }
}
